import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Calculator, FileSignature, Inbox, LayoutDashboard, PlusCircle, Settings, Users } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useLoad, useOwnerApi } from "@/lib/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/bookings", label: "Bookings", icon: Inbox },
  { to: "/dashboard/new", label: "Add ride", icon: PlusCircle },
  { to: "/dashboard/calculator", label: "Calculator", icon: Calculator },
  { to: "/dashboard/customers", label: "Customers", icon: Users },
  { to: "/dashboard/agreements", label: "Agreements", icon: FileSignature },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

export default function DashLayout() {
  const api = useOwnerApi();
  const me = useLoad(() => api.get("/me").then((r) => r.data), [api]);
  return (
    <div className="min-h-screen bg-ink font-body text-mist lg:grid lg:grid-cols-[250px_1fr]">
      <aside className="sticky top-0 z-30 border-b border-gold/20 bg-aubergine/85 backdrop-blur-xl lg:h-screen lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-4 py-3 lg:block lg:px-5 lg:py-6">
          <Logo />
          <div className="lg:hidden"><UserButton /></div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-3" aria-label="Dashboard">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`dash-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => cn("flex min-h-[48px] shrink-0 items-center gap-3 rounded-md px-4 text-[15px] transition-colors duration-200",
                isActive ? "bg-plum text-mist" : "text-mist/80 hover:bg-white/5 hover:text-gold")}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden px-5 pt-6 lg:block"><div className="gold-rule mb-5" /><UserButton /></div>
      </aside>
      <main className="min-w-0 px-4 py-6 md:px-8 lg:py-10">
        {me.loading && <p className="text-mist/70">Loading…</p>}
        {me.error && (
          <div className="mx-auto mt-20 max-w-md text-center" data-testid="dash-access-denied">
            <p className="font-display text-2xl text-gold">Owner access only</p>
            <p className="mt-3 text-[15px] text-mist/75">{me.error}</p>
          </div>
        )}
        {me.data && <Outlet />}
      </main>
    </div>
  );
}

export function PageTitle({ title, sub, children }: { title: string; sub?: string; children?: React.ReactNode }) {
  return (
    <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl text-gold md:text-4xl">{title}</h1>
        {sub && <p className="mt-2 text-[15px] text-mist/70">{sub}</p>}
        <div className="gold-rule mt-4 w-24" />
      </div>
      {children}
    </header>
  );
}
